import type { BenefitDefinitions } from "./benefit.types";

export interface FlatBenefitRow {
  benefitKey: string;
  label: string;
  nested: boolean;
}

/**
 * Flattens a coverage's benefit definitions into the canonical row order used
 * by the AG Grid benefit table: definition order, with each `nested: true`
 * key re-parented directly after the nearest preceding non-nested key (its
 * own definition-order position is otherwise arbitrary — e.g. a typed-amount
 * pair's `Amount` row is defined right after its `Type` row, but a properties
 * object's child keys are not). This mirrors the ordering half of
 * `buildDataRows` without its plan-data-dependent row filtering, which needs
 * a coverage and built column defs this util has no reason to require.
 */
export const flattenBenefitDefinitions = (
  definitions: BenefitDefinitions,
): FlatBenefitRow[] => {
  const keys = Object.keys(definitions);

  const nestedByParent = new Map<string, string[]>();
  keys.forEach((benefitKey, index) => {
    if (!definitions[benefitKey].nested) return;

    for (let i = index - 1; i >= 0; i--) {
      const candidateKey = keys[i];
      if (!definitions[candidateKey].nested) {
        const siblings = nestedByParent.get(candidateKey) ?? [];
        siblings.push(benefitKey);
        nestedByParent.set(candidateKey, siblings);
        break;
      }
    }
  });

  return keys.flatMap((benefitKey) => {
    if (definitions[benefitKey].nested) return [];

    const parentRow: FlatBenefitRow = {
      benefitKey,
      label: definitions[benefitKey].label,
      nested: false,
    };

    const nestedRows: FlatBenefitRow[] = (
      nestedByParent.get(benefitKey) ?? []
    ).map((nestedKey) => ({
      benefitKey: nestedKey,
      label: definitions[nestedKey].label,
      nested: true,
    }));

    return [parentRow, ...nestedRows];
  });
};
